import { ArrowRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimelineMetrics } from '../../types/dashboard';

import forgeService from '../../services/forgeService';
import { useAuthStore } from '../../stores/authStore';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import { Forge, ForgeApplication } from '../../types';

interface TimelineVelocityChartProps {
    data: TimelineMetrics;
    /** Override API counts with frontend-loaded counts (correct when API is not scoped to user). */
    overrides?: { totalUseCases?: number; totalCadrages?: number };
    /** Conversion benchmarks. Defaults: UC→Solution 30%, Solution→App 50%. */
    targets?: { ucToSolution?: number; solutionToApp?: number };
}

/** Color a conversion rate against its target: green ≥ target, amber 60-100% of target, red < 60%. */
const getConversionTone = (value: number, target: number) => {
    if (target <= 0) return 'text-muted-foreground';
    const ratio = value / target;
    if (ratio >= 1) return 'text-emerald-600 dark:text-emerald-400';
    if (ratio >= 0.6) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
};

interface ForgeAppStats {
    totalApps: number;
    deployedApps: number;
    loading: boolean;
}

export const TimelineVelocityChart: React.FC<TimelineVelocityChartProps> = ({ data, overrides, targets }) => {
    const ucToSolutionTarget = targets?.ucToSolution ?? 30;
    const solutionToAppTarget = targets?.solutionToApp ?? 50;
    const { t } = useTranslation();
    const user = useAuthStore((state) => state.user);
    const forgeApiEnabled = useForgeApiEnabled();
    const [forgeStats, setForgeStats] = useState<ForgeAppStats>({ 
        totalApps: 0, 
        deployedApps: 0, 
        loading: true 
    });

    // Fetch real app stats from Forge API
    useEffect(() => {
        if (forgeApiEnabled === null) return;
        const loadForgeAppStats = async () => {
            const organizationId = user?.organization_id;

            if (!organizationId || !forgeApiEnabled) {
                setForgeStats({ totalApps: 0, deployedApps: 0, loading: false });
                return;
            }

            try {
                // Fetch the organization's forge client mapping
                const mapping = await forgeService.getOrganizationClientMapping(organizationId);
                const forgeClientId = mapping.forge_client_id;

                if (!forgeClientId) {
                    setForgeStats({ totalApps: 0, deployedApps: 0, loading: false });
                    return;
                }

                // Fetch forges for this client
                const forges: Forge[] = await forgeService.listForges(forgeClientId);
                
                // Fetch all applications for these forges
                let allApps: ForgeApplication[] = [];
                for (const forge of forges) {
                    try {
                        const apps = await forgeService.listApplications(forge.forge_id);
                        allApps = [...allApps, ...apps];
                    } catch {
                        // Continue if one forge fails
                    }
                }

                const deployedApps = allApps.filter(a => a.status === 'deployed').length;
                setForgeStats({ 
                    totalApps: allApps.length, 
                    deployedApps, 
                    loading: false 
                });
            } catch (err) {
                console.error('Failed to load forge app stats:', err);
                setForgeStats({ totalApps: 0, deployedApps: 0, loading: false });
            }
        };

        loadForgeAppStats();
    }, [user?.organization_id, forgeApiEnabled]);

    const totalUseCases = overrides?.totalUseCases ?? data.total_use_cases;
    const totalCadrages = overrides?.totalCadrages ?? data.total_cadrages;

    const conversionCadrageToApp = totalCadrages > 0
        ? (forgeStats.deployedApps / totalCadrages * 100)
        : 0;

    const stages: Array<{
        key: string;
        label: string;
        value: number | string;
        color: string;
        conversion?: number;
        target?: number;
    }> = [
        {
            key: 'useCases',
            label: t('dashboard.timeline.useCases', 'Use Cases'),
            value: totalUseCases,
            color: 'bg-blue-500',
        },
        {
            key: 'cadrages',
            label: t('dashboard.timeline.framings', 'Solutions'),
            value: data.use_cases_with_cadrage,
            color: 'bg-purple-500',
            conversion: data.conversion_uc_to_cadrage,
            target: ucToSolutionTarget,
        },
        {
            key: 'deployed',
            label: t('dashboard.timeline.apps', 'Apps'),
            value: forgeStats.loading ? '...' : forgeStats.deployedApps,
            color: 'bg-green-500',
            conversion: forgeStats.loading ? undefined : conversionCadrageToApp,
            target: solutionToAppTarget,
        },
    ];

    return (
        <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('dashboard.timeline.title', 'Timeline & Velocity')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
                {t('dashboard.timeline.subtitle', 'UC → Solution → App conversion flow')}
            </p>

            <div className="flex items-center justify-between gap-4">
                {stages.map((stage, index) => (
                    <React.Fragment key={stage.key}>
                        {/* Stage Box */}
                        <div className="flex-1">
                            <div className={`${stage.color} bg-opacity-10 dark:bg-opacity-20 rounded-lg p-4 text-center`}>
                                <div className={`text-3xl font-bold ${stage.color.replace('bg-', 'text-')}`}>
                                    {stage.value}
                                </div>
                                <div className="text-sm font-medium text-foreground mt-2">
                                    {stage.label}
                                </div>
                            </div>
                        </div>

                        {/* Arrow with conversion rate (rate of next stage points back) */}
                        {index < stages.length - 1 && (() => {
                            const next = stages[index + 1];
                            const rate = next.conversion;
                            const target = next.target;
                            return (
                                <div className="flex flex-col items-center gap-1">
                                    <ArrowRight className="h-6 w-6 text-muted-foreground" />
                                    {rate !== undefined && target !== undefined && (
                                        <span
                                            className={`text-xs font-semibold ${getConversionTone(rate, target)}`}
                                            title={t(
                                                'dashboard.timeline.conversionTooltip',
                                                'Taux observé : {{rate}}%. Objectif : {{target}}%.',
                                                { rate: rate.toFixed(1), target }
                                            )}
                                        >
                                            {rate.toFixed(1)}%
                                            <span className="ml-1 font-normal text-muted-foreground">
                                                / {target}%
                                            </span>
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                    </React.Fragment>
                ))}
            </div>

            <p className="mt-4 text-xs text-muted-foreground leading-snug">
                {t(
                    'dashboard.timeline.explanation',
                    'Pourcentage = part de l’étape précédente convertie ; le « / X% » est l’objectif cible. Le code couleur compare la valeur observée à cet objectif.'
                )}
            </p>

        </div>
    );
};
